import {
	Button,
	Flex,
	Image,
	Input,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { containerMaxWXl } from '../contentContainer';
import { IconInfo } from '../../../utils/icons/info';
import { Color } from '../../../theme/color';
import React, { useState } from 'react';
import { useBlockchainProvider } from '../../../providers/Blockchain/blockchainProvider';
import { ethers } from 'ethers';
import { createStandaloneToast } from '@chakra-ui/react';
import { IconReference } from '../../../utils/icons/reference';
import { CloseIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const PromotionPane = () => {
	const [code, setCode] = useState('');
	const { account } = useBlockchainProvider();
	const { toast } = createStandaloneToast();
	const resultPaneDisclosure = useDisclosure();
	const loadingScreenDisclosure = useDisclosure();
	const mobilePopupDisclosure = useDisclosure();
	const history = useNavigate();

	const handleSubmit = async () => {
		if (account === ethers.constants.AddressZero) {
			toast({ title: 'Connect your wallet first' });
		}

		if (account === ethers.constants.AddressZero || !code) {
			return;
		}

		mobilePopupDisclosure.onClose();
		loadingScreenDisclosure.onOpen();

		const resultRaw = await fetch(
			`https://7vmnh637q8.execute-api.eu-west-1.amazonaws.com/prod/giftCard/${account}/${code}`
		);
		const resultIsOk = resultRaw.ok;
		const result = await resultRaw.text();

		loadingScreenDisclosure.onClose();

		if (result === 'Already activated') {
			toast({ title: 'This code has already been used' });
			return;
		}

		if (!resultIsOk) {
			toast({ title: 'Code invalid' });
			return;
		}

		resultPaneDisclosure.onOpen();
	};

	const handleMobilePopup = () => {
		if (!window.ethereum) {
			toast({ title: 'Please connect a blockchain-enabled wallet to the site to redeem' });
			return;
		}

		mobilePopupDisclosure.onOpen();
	};

	const handleNavigateUser = () => {
		resultPaneDisclosure.onClose();
		history(`/user/${account}`);
	};

	const { t } = useTranslation();

	return (
		<>
			<Flex
				fontSize="12px"
				lineHeight="15px"
				borderBottom={`2px solid ${Color.ACTION}`}
				borderTop={{ base: 'none', hashup: `2px solid ${Color.ACTION}` }}
				gap="14px"
				userSelect="none"
			>
				<Flex
					pl={{ base: '16px', hashup: '32px', xl: `calc((100% - ${containerMaxWXl}) / 2)` }}
					fontSize={{ base: '14px', hashup: '28px' }}
					fontWeight="600"
					lineHeight={{ base: '15px', hashup: '31px' }}
					py={{ base: '12px', hashup: '16px' }}
					pr="2em"
					background="action"
					clipPath="polygon(0 0, 100% 0%, calc(100% - 1em) 100%, 0% 100%);"
				>
					<Text color="textAction">
						{t('promotionPanel.getGameFirst')}
						<Text
							as="span"
							color="black !important"
						>
							{t('promotionPanel.getGameSecond')}
						</Text>
						!
					</Text>
				</Flex>
				<Flex
					display={{ base: 'none', hashup: 'flex' }}
					pr={{ base: '16px', hashup: '32px', xl: `calc((100% - ${containerMaxWXl}) / 2)` }}
					lineHeight="19px"
					alignItems="center"
					justifyContent="space-between"
					width="100%"
				>
					<Flex
						gap="16px"
						alignItems="center"
					>
						<IconInfo />
						<Flex direction="column">
							<Text>{t('promotionPanel.infoFirst')}</Text>
							<Text fontWeight="600">{t('promotionPanel.infoSecond')}</Text>
						</Flex>
					</Flex>
					<Flex gap="8px">
						<Input
							border="none"
							placeholder={t('promotionPanel.placeholder')}
							fontSize="12px"
							lineHeight="15px"
							color="black"
							_placeholder={{ color: '#6E6E6E' }}
							bg="white"
							minW="20em"
							maxLength={14}
							onChange={event => setCode(event.target.value)}
						/>
						<Button
							fontSize="12px"
							lineHeight="15px"
							onClick={handleSubmit}
						>
							{t('promotionPanel.getIt')}
						</Button>
					</Flex>
				</Flex>
				<Flex
					display={{ base: 'flex', hashup: 'none' }}
					pr={{ base: '16px', hashup: '32px', xl: `calc((100% - ${containerMaxWXl}) / 2)` }}
					fontSize="14px"
					lineHeight="18px"
					alignItems="center"
					width="100%"
					gap="16px"
					onClick={handleMobilePopup}
				>
					<Text
						textDecoration="underline"
						cursor="pointer"
					>
						Get it now!
					</Text>
					<IconReference />
				</Flex>
			</Flex>

			{/** Loading screen disclosure */}
			<Modal
				isOpen={loadingScreenDisclosure.isOpen}
				onClose={loadingScreenDisclosure.onClose}
				size="full"
			>
				<ModalOverlay bg="#000000C1" />
				<ModalContent
					bg="transparent"
					textAlign="center"
					width="100%"
					height="100%"
					rounded="none"
					margin="0"
					alignItems="center"
					justifyContent="center"
				>
					<ModalBody
						justifyContent="center"
						height="auto"
						minHeight="auto"
						display="flex"
						gap="8px"
						flexDirection="column"
					>
						<Text
							fontWeight="600"
							fontSize="36px"
							lineHeight="48px"
							textAlign="center"
						>
							Processing
						</Text>
						<Flex
							direction="column"
							gap="64px"
							justify="center"
							align="center"
						>
							<Text
								color="#979797"
								fontSize="16px"
								lineHeight="24px"
								maxWidth={{ base: '80%', hashup: '32em' }}
							>
								Redeeming the code for {account}
							</Text>
							<Spinner
								width="72px"
								height="72px"
								thickness="6px"
							/>
						</Flex>
					</ModalBody>
				</ModalContent>
			</Modal>

			{/** Mobile form disclosure */}
			<Modal
				isOpen={mobilePopupDisclosure.isOpen}
				onClose={mobilePopupDisclosure.onClose}
			>
				<ModalOverlay bg="#000000C1" />
				<ModalContent
					bg="#161616"
					boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
					textAlign="center"
					borderRadius="13px"
					border={`1px solid ${Color.ACTION}`}
					alignItems="center"
					justifyContent="center"
					py="8px"
				>
					<ModalHeader
						display="flex"
						alignItems="center"
						color="white"
						fontWeight="600"
						width="100%"
						flexDirection="column"
					>
						<Flex
							width="100%"
							justify="right"
						>
							<Flex
								cursor="pointer"
								justifyContent="center"
								alignItems="center"
								onClick={mobilePopupDisclosure.onClose}
								border="1px solid white"
								borderRadius="50%"
								p="5px"
							>
								<CloseIcon
									w="7px"
									h="7px"
								/>
							</Flex>
						</Flex>
						<Text
							fontSize="20px"
							textAlign="center"
							color="textAction"
						>
							Get your{' '}
							<Text
								as="span"
								color="action"
							>
								free game
							</Text>
							!
						</Text>
					</ModalHeader>
					<ModalBody
						justifyContent="center"
						height="auto"
						minHeight="auto"
						display="flex"
						gap="32px"
						flexDirection="column"
					>
						<Flex
							direction="column"
							gap="48px"
							justify="center"
							align="center"
						>
							<Text
								fontSize="16px"
								lineHeight="24px"
							>
								Jeżeli otrzymałeś kartę z promo codem, wykorzystaj go aby odebrać grę.&nbsp;
								<Text
									as="span"
									fontWeight="600"
								>
									Kod działa jednarazowo i po użyciu nie będzie działać.
								</Text>
							</Text>
							<Flex
								gap="8px"
								direction="column"
								width="100%"
							>
								<Input
									border="none"
									placeholder="Enter your promo code here"
									fontSize="12px"
									lineHeight="15px"
									color="black"
									_placeholder={{ color: '#6E6E6E' }}
									bg="white"
									minW="20em"
									maxLength={14}
									onChange={event => setCode(event.target.value)}
								/>
								<Button
									fontSize="12px"
									lineHeight="15px"
									onClick={handleSubmit}
								>
									Get it!
								</Button>
							</Flex>
						</Flex>
					</ModalBody>
				</ModalContent>
			</Modal>

			{/** Success disclosure */}
			<Modal
				isOpen={resultPaneDisclosure.isOpen}
				onClose={resultPaneDisclosure.onClose}
			>
				<ModalOverlay bg="#000000C1" />
				<ModalContent
					bg="#161616"
					boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
					textAlign="center"
					borderRadius="13px"
					border={`1px solid ${Color.ACTION}`}
					alignItems="center"
					justifyContent="center"
					py="8px"
				>
					<ModalHeader
						display="flex"
						alignItems="center"
						color="white"
						fontWeight="600"
						width="100%"
						flexDirection="column"
					>
						<Flex
							width="100%"
							justify="right"
						>
							<Flex
								cursor="pointer"
								justifyContent="center"
								alignItems="center"
								onClick={resultPaneDisclosure.onClose}
								border="1px solid white"
								borderRadius="50%"
								p="5px"
							>
								<CloseIcon
									w="7px"
									h="7px"
								/>
							</Flex>
						</Flex>
						<Text
							fontSize="20px"
							textAlign="center"
						>
							Gratulacje!
						</Text>
					</ModalHeader>
					<ModalBody
						justifyContent="center"
						height="auto"
						minHeight="auto"
						display="flex"
						gap="32px"
						flexDirection="column"
					>
						<Flex
							direction="column"
							gap="48px"
							justify="center"
							align="center"
						>
							<Text
								fontSize="16px"
								lineHeight="24px"
							>
								W ciagu kilku minut na Twój portfel zostaną przesłąne następujące gry:
							</Text>
							<Flex
								justify="start"
								width="100%"
							>
								<Image
									width="192px"
									src="/icons/promotion_giveaway.svg"
									userSelect="none"
									draggable="false"
								/>
							</Flex>
						</Flex>
						<Button onClick={handleNavigateUser}>Zobacz swoje gry</Button>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
};
